
import { BaseSyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { useDispatch, useSelector } from 'react-redux';
import { accountLoginStart, selectAccountLoginErrMsg, selectAccountLoginLoading, selectIsLoggedIn as selectIsLoggedIn } from '../../../redux/account';
import { selectControls } from '../../../redux/controls';
import Spinner from '../../Spinner/Spinner';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import './PublishLoginModal.scss';
import { checkEmailAsync, processCreateAccountOnPublish, PublishLoginModalProps } from '../models';
import { LoginRequest } from '../../../redux/account/models';

export const PublishLoginModal = ({ open, onClose }: PublishLoginModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const controls = useSelector(selectControls);

  const errMsg = useSelector(selectAccountLoginErrMsg);
  const loginIsLoading = useSelector(selectAccountLoginLoading);

  const successful = useSelector(selectIsLoggedIn);

  ReactModal.setAppElement('#root');

  const form = useRef<HTMLFormElement>(null);

  const [createIsLoading, setCreateIsLoading] = useState<boolean>(false);
  const [createErrMsg, setCreateErrMsg] = useState<string>('');

  const [contentLabel, setContentLabel] = useState<string>('');
  const [displayPassword, setDisplayPassword] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [emailAbortController, setEmailAbortController] = useState(new AbortController());
  const [createSubmitted, setCreateSubmitted] = useState<boolean>(false);
  const [credentials, setCredentials] = useState<LoginRequest>({emailId: '', password: ''});
  const [loginSubmitted, setLoginSubmitted] = useState<boolean>(false);
  const [pwMismatch, setPWMismatch] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const validateEmailOnBlur = useCallback(
    (form: React.RefObject<HTMLFormElement>) =>
      async(formEl? :React.FocusEvent<HTMLInputElement, Element>) => {
      // if invalid email pattern, hide password prompt
        if (!formEl?.currentTarget?.checkValidity()) {
          setIsValid(false);
          setDisplayPassword(false);
          return;
        } else {
          setIsValid(!!form?.current?.checkValidity());
        }

        // check email on blur, with valid email
        if (formEl.currentTarget.checkValidity() && form.current) {
          const formData = new FormData(form.current);
          const formProps = Object.fromEntries(formData);
          emailAbortController.abort();
          const emailAC = new AbortController();
          setEmailAbortController(emailAC);
          if (await checkEmailAsync(formProps['email'] as string, emailAC.signal)) {
            setDisplayPassword(false);
          } else {
            setDisplayPassword(true);
          }
        }
      }, [emailAbortController]);

  const closeModalHandler = useCallback((emailId?: string) => {
    setDisplayPassword(false);
    setIsValid(false);
    setProcessing(false);
    setCreateSubmitted(false);
    setCredentials({emailId: '', password: ''});
    setLoginSubmitted(false);
    setPWMismatch(false);
    setSubmitted(false);
    onClose(emailId);
  }, [onClose]);

  const submitFormHandler = useCallback(async(event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (formEl.current) {
      if (!formEl.current.checkValidity()) {
        setIsValid(false);
        return;
      }
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);

      if (!displayPassword) {
        emailAbortController.abort();
        const emailAC = new AbortController();
        setEmailAbortController(emailAC);
        if (await checkEmailAsync(formProps['email'] as string, emailAC.signal)) {
          const creds = {
            emailId: formProps['email'] as string,
            password: formProps['password'] as string
          };
          setCredentials(creds);
          dispatch(
            accountLoginStart(creds)
          );
          setProcessing(true);
          setLoginSubmitted(true);
          return;
        } else {
          setDisplayPassword(true);
          return;
        }
      } else {
        if (
          formProps['password'] !== formProps['confirm-new-password']
        ) {
          setPWMismatch(true);
          const pwEl = document.getElementById('pub-modal-pw') as HTMLInputElement;
          if (pwEl) {
            pwEl.focus();
            pwEl.select();
          }
        } else {
          setPWMismatch(false);
          const creds = {
            emailId: formProps['email'] as string,
            password: formProps['password'] as string
          };
          setCredentials(creds);
          processCreateAccountOnPublish(creds, setCreateIsLoading, setCreateErrMsg);
          setCreateSubmitted(true);
          setSubmitted(true);
          setProcessing(true);
        }
      }
    }
  }, [dispatch, displayPassword, emailAbortController]);

  // fires on mount and whenever showForgotPassword is toggled
  // sets Header Title
  useEffect(() => {
    if (controls) {
      setContentLabel(controls?.PubOnlineTestHeader?.controlText);
    }
  }, [controls]);

  // fires when form is submitted and createAccount finishes loading
  // fires login request
  useEffect(() => {
    if (submitted && createSubmitted && !createIsLoading && credentials.emailId && credentials.password && !loginSubmitted) {
      dispatch(
        accountLoginStart(credentials)
      );
      setCreateSubmitted(false);
      setLoginSubmitted(true);
    }
  }, [createIsLoading, createSubmitted, credentials, dispatch, loginSubmitted, submitted]);

  // fires when loginSubmitted is true and login callback finished
  // closes modal and submits email
  useEffect(() => {
    if (loginSubmitted && !loginIsLoading) {
      closeModalHandler(credentials.emailId);
    }
  }, [closeModalHandler, credentials.emailId, loginIsLoading, loginSubmitted]);

  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          // onAfterOpen={() => onAfterOpenCallback(winType, cancelEl, okEl)}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={contentLabel}
        >
          {
            !submitted || processing ?
              <>
                <form ref={form} onSubmit={(e) => submitFormHandler(e, form)} className='nqt-modal--container'>
                  { processing && <Spinner/> }
                  <div className="nqt-modal--header-group">
                    <header className='nqt-modal--title'>{contentLabel}</header>
                    <button className='nqt-modal--close-button'
                      type='button'
                      onClick={() => closeModalHandler()}>
                      <DeleteIcon/>
                      <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                    </button>
                  </div>
                  <div className="nqt-modal--border">
                    <section className="nqt-pub-modal--content container">
                      <div className="nqt-modal--form-controls container-fluid">
                        <div className="row row-cols-1 gy-3">
                          <div className="col container-fluid">
                            <TextFormControl id='pub-modal-email'
                              autoComplete="email"
                              containerClass="nqt-modal--text-input"
                              controlName='email'
                              controlType="email"
                              errorText={controls?.LoginValidEmail?.controlText}
                              headerText={controls?.LoginID?.controlText}
                              isValidCallback={() => setIsValid(!!form.current?.checkValidity())}
                              onBlurCallback={validateEmailOnBlur(form)}
                              pattern="^[A-Za-z0-9._%+-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{1,10}$"
                              required={true}
                              setFocus={true}/>
                          </div>
                          {
                            pwMismatch && displayPassword &&
                            <div className="col nqt-modal--error">
                              <span className="nqt-modal--error-message">
                                {
                                  controls?.PasswordMustMatch?.controlText
                                }
                              </span>
                            </div>
                          }
                          {
                            displayPassword &&
                            <div className="col container-fluid">
                              <p className="nqt-change-password-modal--rules"
                                dangerouslySetInnerHTML={{__html: controls?.PasswordInstruct ? controls?.PasswordInstruct?.controlText : ''}}>
                              </p>
                            </div>
                          }
                          <div className="col container-fluid">
                            <TextFormControl id='pub-modal-pw'
                              autoComplete="current-password"
                              containerClass="nqt-modal--text-input password"
                              controlName='password'
                              controlType="password"
                              headerText={controls?.LoginPwd?.controlText}
                              isValidCallback={() => setIsValid(!!form.current?.checkValidity())}
                              pattern="^(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z])(?=\D*\d)(?=[^-!@#$%^&*()_+=]*[-!@#$%^&*()_+=]).{8,50}$"
                              required={true}/>
                          </div>
                          {
                            displayPassword &&
                            <div className="col container-fluid">
                              <TextFormControl id='pub-modal-confirm-new-pw'
                                autoComplete="new-password"
                                containerClass="nqt-modal--text-input"
                                controlName='confirm-new-password'
                                controlType="password"
                                headerText={controls?.LoginPwd2?.controlText}
                                isValidCallback={() => setIsValid(!!form.current?.checkValidity())}
                                pattern="^.{8,50}$"
                                required={true} />
                            </div>
                          }
                        </div>
                      </div>
                    </section>
                    <hr />
                    <div className="nqt-modal--buttons col-9 offset-3">
                      <button className="nqt-button--cancel col-4 offset-3"
                        type='button'
                        onClick={() => closeModalHandler()}>{controls?.ModalCancel?.controlText}</button>
                      <button className="nqt-button--submit col-4"
                        disabled={!isValid}
                        type="submit"
                        onClick={(e) => submitFormHandler(e, form)}>{controls?.Confirm?.controlText}</button>
                    </div>
                  </div>
                </form>
              </>
              :
              <>
                <div className='nqt-modal--container'>
                  <div className="nqt-modal--header-group">
                    <header className='nqt-modal--title'>{contentLabel}</header>
                    <button className='nqt-modal--close-button'
                      ref={(el) => el?.focus()}
                      onClick={() => closeModalHandler()}>
                      <DeleteIcon/>
                      <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                    </button>
                  </div>
                  <div className="nqt-modal--border">
                    <section className="nqt-modal--content">
                      {
                        successful ?
                          <div className="nqt-modal--description">
                            {
                              controls?.LoginSuccess?.controlText
                            }
                          </div> :
                          <div className="nqt-modal--description">
                            {
                              errMsg ?
                                errMsg as string :
                                createErrMsg ?
                                  createErrMsg :
                                  controls?.StandardErrorMsg?.controlText
                            }
                          </div>
                      }
                    </section>
                  </div>
                </div>
              </>
          }
        </ReactModal>
      }
    </>
  );
};

export default PublishLoginModal;